package com.rosario.juridico.premium

import kotlinx.datetime.Clock

/**
 * Sistema de telemetría mínima para observabilidad del sistema premium.
 * Desacoplado para facilitar la integración con Firebase Analytics o similares.
 */
object PremiumTelemetry {

    fun logEvent(event: String, params: Map<String, String> = emptyMap()) {
        val timestamp = Clock.System.now().toString()
        // Por ahora enviamos a consola, en el futuro se conecta con un motor de Analytics
        println("[Telemetry] [$timestamp] Event: $event | Params: $params")
    }

    fun logSyncSuccess(uid: String, plan: String) {
        logEvent("premium_sync_success", mapOf("uid" to uid, "plan" to plan))
    }

    fun logSyncFailed(uid: String, error: String) {
        logEvent("premium_sync_failed", mapOf("uid" to uid, "error" to error))
    }

    fun logRestoreSuccess(uid: String) {
        logEvent("premium_restore_success", mapOf("uid" to uid))
    }

    fun logRestoreFailed(uid: String, error: String) {
        logEvent("premium_restore_failed", mapOf("uid" to uid, "error" to error))
    }

    fun logGracePeriodEntered(uid: String) {
        logEvent("premium_grace_period_entered", mapOf("uid" to uid))
    }

    fun logPremiumExpired(uid: String) {
        logEvent("premium_expired", mapOf("uid" to uid))
    }

    fun logRetry(uid: String, attempt: Int) {
        logEvent("premium_retry", mapOf("uid" to uid, "attempt" to attempt.toString()))
    }

    fun logRedundantRefresh(uid: String, reason: String) {
        logEvent("premium_redundant_refresh", mapOf("uid" to uid, "reason" to reason))
    }

    fun logDuplicateRestore(uid: String) {
        logEvent("premium_duplicate_restore", mapOf("uid" to uid))
    }

    fun logRaceConditionDetected(operation: String) {
        logEvent("premium_race_condition", mapOf("operation" to operation))
    }

    fun logSyncLoopDetected(uid: String, syncCount: Int) {
        logEvent("premium_sync_loop", mapOf("uid" to uid, "count" to syncCount.toString()))
    }

    fun logInconsistencyDetected(local: String, server: String) {
        logEvent("premium_consistency_error", mapOf("local" to local, "server" to server))
    }

    fun logCacheStaleDetected(uid: String, ageMs: Long) {
        logEvent("premium_stale_cache", mapOf("uid" to uid, "age_ms" to ageMs.toString()))
    }

    fun logForegroundRefresh(uid: String) {
        logEvent("premium_foreground_refresh", mapOf("uid" to uid))
    }

    /**
     * 1. Registra cambios en el estado de red detectado por el 'reconnect refresh'.
     */
    fun logConnectivityChange(isOnline: Boolean) {
        val state = if (isOnline) "ONLINE" else "OFFLINE"
        logEvent("network_connectivity_change", mapOf("status" to state))
    }

    /**
     * 2. Registra si el derecho proviene de caché o fue validado por Firestore.
     */
    fun logEntitlementResolution(entitlement: PremiumEntitlement) {
        val sourceLabel = when (entitlement.consistency) {
            EntitlementConsistencyState.SERVER_SYNCED -> "FIRESTORE_FRESH"
            EntitlementConsistencyState.STALE_CACHE -> "STALE_CACHE_OFFLINE"
            EntitlementConsistencyState.LOCAL_ONLY -> "LOCAL_CACHE_STARTUP"
            EntitlementConsistencyState.GRACE_PERIOD -> "GRACE_PERIOD_ACTIVE"
            EntitlementConsistencyState.RESTORED -> "FIRESTORE_RESTORED"
            EntitlementConsistencyState.EXPIRED -> "EXPIRED"
        }
        logEvent("premium_source_resolved", mapOf(
            "plan" to entitlement.plan,
            "source" to sourceLabel,
            "is_active" to entitlement.isActiveManual.toString()
        ))
    }

    /**
     * 3. Registra el diagnóstico del Grace Period de 48h.
     */
    fun logGracePeriodDiagnosis(lastSyncTime: Long) {
        val now = Clock.System.now().toEpochMilliseconds()
        val elapsed = now - lastSyncTime
        val gracePeriodLimit = 172_800_000L // 48h
        val remaining = gracePeriodLimit - elapsed
        
        logEvent("premium_grace_period_diag", mapOf(
            "now_ms" to now.toString(),
            "last_sync_ms" to lastSyncTime.toString(),
            "elapsed_ms" to elapsed.toString(),
            "remaining_ms" to remaining.toString(),
            "is_valid" to (elapsed < gracePeriodLimit && lastSyncTime > 0).toString()
        ))
    }
}

/**
 * Extensiones de diagnóstico para PremiumManager (QA y Resiliencia).
 */
fun PremiumManager.reportConnectivity(isOnline: Boolean) {
    PremiumTelemetry.logConnectivityChange(isOnline)
}

fun PremiumManager.reportCurrentSource() {
    PremiumTelemetry.logEntitlementResolution(this.entitlement)
}

fun PremiumManager.reportGracePeriodStatus() {
    PremiumTelemetry.logGracePeriodDiagnosis(this.lastSyncTime)
}

/**
 * Gestiona el refresco automático al detectar reconexión (Fase 4.1 QA).
 */
suspend fun PremiumManager.handleReconnectRefresh(isOnline: Boolean) {
    PremiumTelemetry.logConnectivityChange(isOnline)
    if (isOnline && (entitlement.consistency == EntitlementConsistencyState.STALE_CACHE || 
                    entitlement.consistency == EntitlementConsistencyState.LOCAL_ONLY)) {
        syncWithServer()
    }
}
